/**
 * The homescreen watching callout (#94, §12.1) — status, today's figures and the snooze buttons,
 * directly under the buttons row.
 *
 * A **passive** surface: it renders only what #68 hands it, and nothing at all when there is no
 * callout to show (master off, or the query still pending / failed). Both sentences are server-built
 * and printed verbatim — no browser clock ever computes a watching time (§12.5). Two groups: the
 * status dot, headline and figures on the left, the buttons pushed to the right. The one branch this
 * component owns is `status === 'snoozed'`, which swaps the stats + snooze row for a single Resume.
 *
 * It polls #68 on a minute, because "132 checks today" moves with every tick and no SSE event says
 * so; `watch.detection` already invalidates `QK.watch` when something is actually caught.
 */

import type { WatchSnoozePreset } from '@lw/shared';
import { describeError } from '../../api/client';
import { cx } from '../../components';
import { SnoozePicks } from '../settings/SnoozePicks';
import { useWatchQuery, useWatchSnooze } from '../settings/useWatch';

/** How often the callout re-reads #68 — the today figures move with the tick, not with an event. */
export const WATCH_CALLOUT_POLL_MS = 60_000;

export function WatchCallout() {
  const query = useWatchQuery(true, { refetchIntervalMs: WATCH_CALLOUT_POLL_MS });
  const snooze = useWatchSnooze();
  const callout = query.data?.callout ?? null;

  if (callout === null) return null;

  return (
    <div
      className={cx('card', 'home-watch-callout', `home-watch-callout--${callout.status}`)}
      role="status"
    >
      <div className="home-watch-callout__main">
        <span className="home-watch-callout__dot" aria-hidden="true" />
        <span className="home-watch-callout__headline">{callout.headline}</span>
        {callout.status === 'snoozed' ? null : (
          <span className="home-watch-callout__stats">{callout.statsLine}</span>
        )}
      </div>
      <div className="home-watch-callout__actions">
        {callout.status === 'snoozed' ? (
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            disabled={snooze.isPending}
            onClick={() => snooze.mutate(null)}
          >
            Resume
          </button>
        ) : (
          <SnoozePicks
            onPick={(preset: WatchSnoozePreset) => snooze.mutate(preset)}
            disabled={snooze.isPending}
            ariaLabel="Snooze watching"
          />
        )}
      </div>
      {snooze.isError ? (
        <span className="settings-bad" role="alert">
          {describeError(snooze.error)}
        </span>
      ) : null}
    </div>
  );
}

export default WatchCallout;
